/* A minimal ZIP reader/writer.
 *
 * Hand-rolled to keep the app dependency-free. Compression uses the platform's
 * CompressionStream when available and falls back to stored (uncompressed)
 * entries, which every unzipper accepts.
 */

const LOCAL_SIG = 0x04034b50;
const CENTRAL_SIG = 0x02014b50;
const EOCD_SIG = 0x06054b50;

const enc = new TextEncoder();
const dec = new TextDecoder();

/* ------------------------------------------------------------------ crc */

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[i] = c >>> 0;
  }
  return t;
})();

function crc32(bytes) {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/* ------------------------------------------------------------ deflation */

const canDeflate = typeof CompressionStream === 'function';
const canInflate = typeof DecompressionStream === 'function';

async function pipe(bytes, stream) {
  const source = new ReadableStream({ start(c) { c.enqueue(bytes); c.close(); } });
  return new Uint8Array(await new Response(source.pipeThrough(stream)).arrayBuffer());
}
const deflateRaw = bytes => pipe(bytes, new CompressionStream('deflate-raw'));
const inflateRaw = bytes => pipe(bytes, new DecompressionStream('deflate-raw'));

/* --------------------------------------------------------------- dos ts */

function dosTime(date) {
  const t = ((date.getHours() & 31) << 11) | ((date.getMinutes() & 63) << 5) | ((date.getSeconds() / 2) & 31);
  const d = (((date.getFullYear() - 1980) & 127) << 9) | (((date.getMonth() + 1) & 15) << 5) | (date.getDate() & 31);
  return { time: t, date: d };
}

/* ---------------------------------------------------------------- write */

/**
 * @param {{path: string, text?: string, bytes?: Uint8Array, date?: Date}[]} files
 * @returns {Promise<Blob>}
 */
export async function zip(files, { compress = true } = {}) {
  const parts = [];
  const central = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = enc.encode(file.path);
    const raw = file.bytes ?? enc.encode(file.text ?? '');
    const crc = crc32(raw);

    let method = 0;
    let body = raw;
    if (compress && canDeflate && raw.length > 64) {
      const packed = await deflateRaw(raw);
      if (packed.length < raw.length) { method = 8; body = packed; }
    }

    const { time, date } = dosTime(file.date || new Date());

    const header = new Uint8Array(30 + nameBytes.length);
    const hv = new DataView(header.buffer);
    hv.setUint32(0, LOCAL_SIG, true);
    hv.setUint16(4, 20, true);
    hv.setUint16(6, 0x0800, true);           // UTF-8 filenames
    hv.setUint16(8, method, true);
    hv.setUint16(10, time, true);
    hv.setUint16(12, date, true);
    hv.setUint32(14, crc, true);
    hv.setUint32(18, body.length, true);
    hv.setUint32(22, raw.length, true);
    hv.setUint16(26, nameBytes.length, true);
    hv.setUint16(28, 0, true);
    header.set(nameBytes, 30);

    parts.push(header, body);

    const cd = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(cd.buffer);
    cv.setUint32(0, CENTRAL_SIG, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, method, true);
    cv.setUint16(12, time, true);
    cv.setUint16(14, date, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, body.length, true);
    cv.setUint32(24, raw.length, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint32(42, offset, true);
    cd.set(nameBytes, 46);
    central.push(cd);

    offset += header.length + body.length;
  }

  const cdSize = central.reduce((a, c) => a + c.length, 0);
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, EOCD_SIG, true);
  ev.setUint16(8, files.length, true);
  ev.setUint16(10, files.length, true);
  ev.setUint32(12, cdSize, true);
  ev.setUint32(16, offset, true);

  return new Blob([...parts, ...central, eocd], { type: 'application/zip' });
}

/* ----------------------------------------------------------------- read */

async function toBytes(input) {
  if (input instanceof Uint8Array) return input;
  if (input instanceof ArrayBuffer) return new Uint8Array(input);
  if (typeof input?.arrayBuffer === 'function') return new Uint8Array(await input.arrayBuffer());
  return new Uint8Array(await new Response(input).arrayBuffer());
}

/**
 * @param {Blob|ArrayBuffer|Uint8Array} input
 * @returns {Promise<{path: string, bytes: Uint8Array, text: string}[]>}
 */
export async function unzip(input) {
  const buf = await toBytes(input);
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);

  let eocd = -1;
  for (let i = buf.length - 22; i >= Math.max(0, buf.length - 65557); i--) {
    if (view.getUint32(i, true) === EOCD_SIG) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error('Not a zip file (no end-of-central-directory record).');

  const count = view.getUint16(eocd + 10, true);
  let p = view.getUint32(eocd + 16, true);
  const out = [];

  for (let i = 0; i < count; i++) {
    if (view.getUint32(p, true) !== CENTRAL_SIG) throw new Error('Corrupt zip: bad central directory entry.');
    const method = view.getUint16(p + 10, true);
    const crc = view.getUint32(p + 16, true);
    const compSize = view.getUint32(p + 20, true);
    const rawSize = view.getUint32(p + 24, true);
    const nameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const localOff = view.getUint32(p + 42, true);
    const path = dec.decode(buf.subarray(p + 46, p + 46 + nameLen));
    p += 46 + nameLen + extraLen + commentLen;

    if (view.getUint32(localOff, true) !== LOCAL_SIG) throw new Error('Corrupt zip: bad local header for ' + path);
    const lNameLen = view.getUint16(localOff + 26, true);
    const lExtraLen = view.getUint16(localOff + 28, true);
    const start = localOff + 30 + lNameLen + lExtraLen;
    const body = buf.subarray(start, start + compSize);

    if (path.endsWith('/')) continue;                       // directory entry

    let bytes;
    if (method === 0) bytes = body;
    else if (method === 8) {
      if (!canInflate) throw new Error('This browser cannot read compressed zips. Unzip it and import the folder instead.');
      bytes = await inflateRaw(body);
    } else throw new Error('Unsupported compression method ' + method + ' in ' + path);

    if (bytes.length !== rawSize || crc32(bytes) !== crc) throw new Error('Checksum mismatch in ' + path);
    out.push({ path, bytes, text: dec.decode(bytes) });
  }

  return out;
}
